// src/components/achievements/StepList.tsx
import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

type Step = {
  id: number;
  name?: string;
  description?: string;
  completed?: boolean;
  progress?: {
    current: number;
    total: number;
    percentage?: number;
  };
};

type Props = {
  steps: Step[];
  showProgress?: boolean;
  isThresholdAchievement?: boolean; // For multi-step achievements with thresholds
};

const StepList: React.FC<Props> = ({ 
  steps, 
  showProgress = true,
  isThresholdAchievement = false
}) => {
  const { t } = useLanguage();

  // Helper to check if a text is just a generic "Objective" or "Tier X" label
  const isGenericObjective = (text?: string): boolean => {
    if (!text) return false;
    // Add more variations that might be considered generic
    const genericTexts = ['Objective', 'objective', 'Objective.', 'Step', 'step'];
    const tierPattern = /^Tier\s+\d+$/i;
    return genericTexts.includes(text.trim()) || tierPattern.test(text.trim());
  };

  // Get the appropriate text for the objective
  const getObjectiveText = (step: Step): string => {
    // If description is available and not generic, prioritize it
    if (step.description && !isGenericObjective(step.description)) {
      return step.description;
    }
    
    // If name is available and not generic, use it
    if (step.name && !isGenericObjective(step.name)) {
      return step.name;
    }
    
    // If both are generic, use the translated text
    return t('achievements.objectiveCompleted');
  };

  // Handle empty steps case
  if (!steps || steps.length === 0) {
    return (
      <div className="bg-gray-700 rounded p-4 text-gray-300">
        {t('achievements.noObjectives')}
      </div>
    );
  }

  // Immer die Schritte anzeigen, auch für nicht authentifizierte Benutzer
  // Nur die Fortschrittsanzeige wird basierend auf showProgress gehandhabt
  if (isThresholdAchievement) {
    // Sort steps by completion value (assuming higher values are later thresholds)
    const sortedSteps = [...steps].sort((a, b) => {
      const aValue = a.progress?.total || 0;
      const bValue = b.progress?.total || 0;
      return aValue - bValue;
    });

    return (
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-white mb-4">{t('achievements.tiers')}</h2>
        <div className="space-y-6">
          {sortedSteps.map((step, index) => {
            const current = step.progress?.current ?? 0;
            const total = step.progress?.total ?? 1;
            const isCompleted = step.completed || (current >= total);
            const percentage = isCompleted ? 100 : 
  (step.progress?.percentage !== undefined ? 
    step.progress.percentage : 
    (step.progress && total > 0 ? 
      Math.min(99, Math.round((current * 100) / total)) : 0));
            
            // For binary completion objectives, ensure consistent display
            const displayCurrent = isCompleted ? 1 : current;
            
            return (
              <div key={step.id} className="relative">
                {/* Progress bar background */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-700"></div>
                
                {/* Step content */}
                <div className="relative flex items-start">
                  <div className="mr-4 flex-shrink-0 flex items-center">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full 
                      ${showProgress && isCompleted ? 'bg-destiny-green' : 'bg-gray-700'}`}
                    >
                      {showProgress && isCompleted ? (
                        <CheckCircleIcon className="h-6 w-6 text-white" />
                      ) : (
                        <span className="text-white">{index + 1}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-grow">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-white font-medium">
                          {`${t('achievements.tier')} ${index + 1}`}
                        </p>
                        <p className="text-gray-300 text-sm">
                          {getObjectiveText(step)}
                        </p>
                      </div>
                      
                      {/* Nur für authentifizierte Benutzer anzeigen */}
                      {showProgress && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${isCompleted ? 'bg-destiny-green text-white' : 'bg-gray-600 text-gray-300'}`}
                        >
                          {total} {t('achievements.required')}
                        </span>
                      )}
                    </div>
                    
                    {/* Progress bar - nur für authentifizierte Benutzer */}
                    {showProgress && (
                      <div className="mt-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-400">
                            {isCompleted ? '1/1' : `${displayCurrent}/${total}`}
                          </span>
                          <span className={isCompleted ? 'text-destiny-green' : 'text-destiny-gold'}>
                            {percentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-1.5">
                          <div
                            className={`${isCompleted ? 'bg-destiny-green' : 'bg-destiny-blue'} h-1.5 rounded-full`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Regular step list - always show steps, but conditionally show progress
  return (
    <div className="bg-gray-800 rounded-lg p-6 mb-6">
      <h2 className="text-xl font-bold text-white mb-4">{t('achievements.objectives')}</h2>

      <div className="space-y-4">
      {steps.map((step, index) => {
  // Ensure we have valid progress values
  const current = step.progress?.current ?? 0;
  const total = step.progress?.total ?? 1;
  
  // Use the completed property directly or calculate it from progress
  const isCompleted = step.completed || (step.progress && 
                                      current >= total);
  
  // FIXED: Calculate percentage correctly based on current/total
  const percentage = isCompleted ? 100 : 
    (step.progress && total > 0 ? 
     Math.min(99, Math.round((current * 100) / total)) : 0);

  // For binary completion objectives (without actual progress tracking)
  // We'll create a consistent display
  const displayProgress = {
    current: isCompleted ? 1 : (step.progress?.current || 0),
    total: step.progress?.total || 1
  };

  return (
    <div key={step.id} className="flex items-center">
      {/* Component layout continues... */}
      <div className="mt-2">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-400">
            {isCompleted ? '1/1' : `${displayProgress.current}/${displayProgress.total}`}
          </span>
          <span className={isCompleted ? 'text-destiny-green' : 'text-destiny-gold'}>
            {/* FIXED: Use calculated percentage here */}
            {percentage}%
          </span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-1.5">
          <div
            className={`${isCompleted ? 'bg-destiny-green' : 'bg-destiny-blue'} h-1.5 rounded-full`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
})}
      </div>
    </div>
  );
};

export default StepList;